import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { Observable, tap, catchError, BehaviorSubject, of } from 'rxjs';

const SETUP_COMPLETE_KEY = 'aivor_setup_complete';

@Injectable({
  providedIn: 'root'
})
export class SetupService {
  private setupCompleteStatus = new BehaviorSubject<boolean | null>(null);
  public setupCompleteStatus$ = this.setupCompleteStatus.asObservable();

  constructor(private http: HttpClient) {
    // Initialize from local storage if available
    const savedStatus = localStorage.getItem(SETUP_COMPLETE_KEY);
    if (savedStatus !== null) {
      this.setupCompleteStatus.next(savedStatus === 'true');
    }
  }

  checkSetupStatus(forceRefresh: boolean = true): Observable<{ isSetupComplete: boolean }> {
    // If not forcing refresh and we have a cached value, return it
    if (!forceRefresh) {
      const localStatus = localStorage.getItem(SETUP_COMPLETE_KEY);
      if (localStatus !== null) {
        const isComplete = localStatus === 'true';
        this.setupCompleteStatus.next(isComplete);
        return of({ isSetupComplete: isComplete });
      }
    }

    // Get auth token and set up headers
    const token = localStorage.getItem('authToken');
    const headers = {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };

    // Always check with the server
    const url = `${environment.apiUrl}/api/Setup/IsSetupComplete`;
    return this.http.get<{ isSetupComplete: boolean }>(url, { headers }).pipe(
      tap(response => {
        localStorage.setItem(SETUP_COMPLETE_KEY, String(response.isSetupComplete));
        this.setupCompleteStatus.next(response.isSetupComplete);
      }),
      catchError(error => {
        //console.error('Error checking setup status:', error);
        // If we have a cached value, use it as fallback, otherwise default to false
        const cachedStatus = localStorage.getItem(SETUP_COMPLETE_KEY);
        const fallbackStatus = cachedStatus === 'true';
        if (cachedStatus === null) {
          localStorage.setItem(SETUP_COMPLETE_KEY, 'false');
          this.setupCompleteStatus.next(false);
        }
        return of({ isSetupComplete: fallbackStatus });
      })
    );
  }

  getCurrentSetupStatus(): boolean {
    return this.setupCompleteStatus.value ?? false;
  }

  completeSetup(): void {
    localStorage.setItem(SETUP_COMPLETE_KEY, 'true');
    this.setupCompleteStatus.next(true);
  }
}
